import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { refreshToken } from '@shared/store/api/baseApi';
import { UnauthenticatedPath } from '@shared/constants/routes.constants';

const FALLBACK_MESSAGE = 'Could not complete the sign-in. Please try again.';

/** Mirrors `SsoError` on the API - the codes it puts into `?sso_error=`. */
const ssoErrorMessages: Record<string, string> = {
  access_denied: 'Your identity provider refused the sign-in.',
  expired: 'The sign-in took too long. Please try again.',
  no_mapping: 'Your directory groups do not grant access to Callevate.',
  mapping_conflict:
    'Your directory groups map to more than one company. Please contact your administrator.',
  account_conflict: 'An account with this email already belongs to another company.',
  provider_error: FALLBACK_MESSAGE,
};

/**
 * Picks a federated sign-in up where the API left it: the refresh cookie is
 * already in the browser, so all that remains is trading it for an access
 * token. On success nothing needs navigating - the token flips the router over
 * to the authorized routes on its own.
 */
export const useSsoCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [errorMsg, setErrorMsg] = useState('');
  const hasStarted = useRef(false);

  const ssoError = searchParams.get('sso_error');

  useEffect(() => {
    // Guards against the double invocation of StrictMode in development.
    if (hasStarted.current) return;
    hasStarted.current = true;

    if (ssoError) {
      setErrorMsg(ssoErrorMessages[ssoError] ?? FALLBACK_MESSAGE);
      return;
    }

    void refreshToken({ silent: true }).then((accessToken) => {
      if (!accessToken) setErrorMsg(FALLBACK_MESSAGE);
    });
  }, [ssoError]);

  const backToSignIn = () => navigate(UnauthenticatedPath.SIGN_IN, { replace: true });

  return { errorMsg, backToSignIn };
};
